import { Text } from "@clipboard-health/ui-react";
import { Alert, Box, Button, CircularProgress, Link, Stack, TextField } from "@mui/material";
import { type ReactElement, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

interface LoginFormProps {
  isSubmitting: boolean;
  error?: string;
  onSubmit: (email: string, password: string) => void;
  onForgotPassword: () => void;
}

export function LoginForm({
  isSubmitting,
  error,
  onSubmit,
  onForgotPassword,
}: LoginFormProps): ReactElement {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const location = useLocation();
  const requestedUrl = new URLSearchParams(location.search).get("requestedUrl");

  return (
    <Stack spacing={2.5}>
      <Text align="center" variant="h4">
        Sign in with email and password
      </Text>
      <Alert severity="info" sx={{ textAlign: "left" }}>
        If you do not have a password yet, you will need to create one using the &quot;Forgot
        password?&quot; link below.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ textAlign: "left" }}>
          {error}
        </Alert>
      )}
      <Box
        noValidate
        component="form"
        onSubmit={(formEvent: React.FormEvent) => {
          formEvent.preventDefault();
          onSubmit(email.trim(), password);
        }}
      >
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            autoComplete="email"
            disabled={isSubmitting}
            label="Email Address"
            size="small"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <TextField
            fullWidth
            required
            autoComplete="current-password"
            disabled={isSubmitting}
            label="Password"
            size="small"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <Button
            fullWidth
            disabled={isSubmitting || !email.trim() || !password}
            type="submit"
            variant="contained"
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Sign In"}
          </Button>
          <Link
            component="button"
            sx={{ alignSelf: "center" }}
            type="button"
            underline="hover"
            variant="body2"
            onClick={onForgotPassword}
          >
            Forgot password?
          </Link>
          <Link
            component={RouterLink}
            sx={{ alignSelf: "center" }}
            to={`/enter${requestedUrl ? `?requestedUrl=${encodeURIComponent(requestedUrl)}` : ""}`}
            underline="hover"
            variant="body2"
          >
            Sign in with email link instead
          </Link>
        </Stack>
      </Box>
    </Stack>
  );
}
